import type { QuickPoll, QuickPollVariant } from "@/types";

type Translate = (key: string) => string;

export type ParticipationGate =
  | "registered-login"
  | "guest-login"
  | "unverified-user";

export interface Participation {
  allowOnlyRegisteredParticipation: boolean;
  gate: ParticipationGate | null;
}

export interface PollAlert {
  message: string;
  status: "error" | "warning";
}

export interface AuthRoutes {
  login: string;
  register: string;
  target: string;
}

interface EmbedContext {
  isEmbed: boolean;
  isInsideIframe: boolean;
  isInsideIframeAndOnCurrentUrl: boolean;
}

interface GetAuthRoutesOptions {
  projectId?: string;
  quickPollId?: string;
  variant?: QuickPollVariant;
}

const isWidgetsOrEmbedPath = (path: string): boolean =>
  path.includes("/embeds/") || /(?:^|\/)widgets(?:\/|$)/.test(path);

interface GetParticipationParams {
  quickPoll: Partial<QuickPoll>;
  isRegisteredUserLoggedIn: boolean;
  isGuestParticipationEnabled: boolean;
  isUnverifiedUserLoggedIn?: boolean;
}

export const getAlert = (
  quickPoll: Partial<QuickPoll>,
  error: string | null,
  translate: Translate
): PollAlert | null => {
  if (quickPoll.state === "draft") {
    return {
      status: "warning",
      message: translate("quickPoll.draftError")
    };
  }

  if (quickPoll.state === "archived") {
    return {
      status: "warning",
      message:
        quickPoll.archivalReasonMessage || translate("quickPoll.archivedError")
    };
  }

  if (error) {
    return {
      status: "error",
      message: translate("quickPoll.fetchError")
    };
  }

  return null;
};

export const isUnverifiedParticipation = (
  allowUnverifiedParticipation?: boolean | null,
  isGuestParticipationEnabled?: boolean
): boolean => !!allowUnverifiedParticipation && !isGuestParticipationEnabled;

export const usesAnonymousVoteMemory = (
  isAnonymous: boolean,
  allowUnverifiedParticipation?: boolean | null,
  isGuestParticipationEnabled?: boolean
): boolean =>
  isAnonymous &&
  !isUnverifiedParticipation(
    allowUnverifiedParticipation,
    isGuestParticipationEnabled
  );

export const isUnverifiedVoter = (
  isUnverifiedUserLoggedIn?: boolean,
  isRegisteredUserLoggedIn?: boolean
): boolean => !!isUnverifiedUserLoggedIn && !isRegisteredUserLoggedIn;

export const getParticipation = ({
  quickPoll,
  isRegisteredUserLoggedIn,
  isGuestParticipationEnabled,
  isUnverifiedUserLoggedIn = false
}: GetParticipationParams): Participation => {
  const { allowAnonymousParticipation, allowUnverifiedParticipation } =
    quickPoll;

  const allowOnlyRegisteredParticipation =
    !allowUnverifiedParticipation && !allowAnonymousParticipation;
  const guestParticipationAllowed =
    isGuestParticipationEnabled && !!allowUnverifiedParticipation;
  const unverifiedParticipationAllowed = isUnverifiedParticipation(
    allowUnverifiedParticipation,
    isGuestParticipationEnabled
  );

  if (
    isRegisteredUserLoggedIn ||
    allowAnonymousParticipation ||
    (isUnverifiedUserLoggedIn && !!allowUnverifiedParticipation)
  ) {
    return {
      allowOnlyRegisteredParticipation,
      gate: null
    };
  }

  if (guestParticipationAllowed) {
    return {
      allowOnlyRegisteredParticipation,
      gate: "guest-login"
    };
  }

  if (unverifiedParticipationAllowed) {
    return {
      allowOnlyRegisteredParticipation,
      gate: "unverified-user"
    };
  }

  return {
    allowOnlyRegisteredParticipation,
    gate: "registered-login"
  };
};

/** Registered login, guest login, and a project panel disable the poll controls. */
export const isPollFormDisabled = ({
  gate,
  visibilityMode,
  canContribute
}: {
  gate: ParticipationGate | null;
  visibilityMode?: string | null;
  canContribute?: boolean;
}): boolean =>
  gate != null || (visibilityMode === "Panelled" && canContribute === false);

export const pollIdsMatch = (
  pollId: string | number | undefined,
  routePollId: string,
  permalink?: string | null
): boolean => {
  if (!routePollId) return false;

  const routeId = String(routePollId);

  if (pollId != null && String(pollId) === routeId) return true;

  return permalink != null && String(permalink) === routeId;
};

export type PollVoteSessionOwner = "anonymous" | `user:${string}`;

export const VOTE_STORAGE_PREFIX = "quick-poll-vote:";

const voteStorageKey = (projectId: string, quickPollId: string): string =>
  `${VOTE_STORAGE_PREFIX}${projectId}:${quickPollId}`;

/**
 * Figures out who the current vote session belongs to, so we know whether to
 * use the API or localStorage for vote memory.
 *
 * - Not logged in → `"anonymous"` (votes remembered in localStorage).
 * - Logged in → `"user:<id>"` (or `"user:session"` if id is missing), so the
 *   vote is bound to that account via the API instead of localStorage.
 */
export const getPollVoteSessionOwner = ({
  isRegisteredUserLoggedIn,
  userId,
  userType
}: {
  isRegisteredUserLoggedIn?: boolean;
  userId?: string | number | null;
  userType?: string | null;
}): PollVoteSessionOwner => {
  const isLoggedIn =
    !!isRegisteredUserLoggedIn ||
    userType === "User" ||
    (userId != null && userId !== "");

  if (!isLoggedIn) return "anonymous";
  return `user:${userId != null && userId !== "" ? String(userId) : "session"}`;
};

export const isAnonymousVoteOwner = (owner: PollVoteSessionOwner): boolean =>
  owner === "anonymous";

/** Anonymous-only UX cache. Callers must not invoke for session users. */
export const savePollVoteLocally = (
  projectId: string,
  quickPollId: string,
  optionId: string | number
): void => {
  if (typeof window === "undefined" || !projectId || !quickPollId) return;

  try {
    localStorage.setItem(
      voteStorageKey(projectId, quickPollId),
      JSON.stringify({ optionId })
    );
  } catch (error) {
    // Private browsing / quota — vote still bound server-side via cookie.
    console.warn("Failed to save poll vote to localStorage", error);
  }
};

export const getStoredPollVote = (
  projectId: string,
  quickPollId: string
): { optionId: string | number } | null => {
  if (typeof window === "undefined" || !projectId || !quickPollId) return null;

  try {
    const raw = localStorage.getItem(voteStorageKey(projectId, quickPollId));
    if (!raw) return null;

    const parsed = JSON.parse(raw) as { optionId?: string | number };
    if (parsed?.optionId == null) return null;

    return { optionId: parsed.optionId };
  } catch {
    return null;
  }
};

/** Clear on auth session change (login/logout) inside quickpoll. */
export const clearAllPollVotesLocally = (): void => {
  if (typeof window === "undefined") return;

  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(VOTE_STORAGE_PREFIX)) keysToRemove.push(key);
    }
    keysToRemove.forEach((key) => localStorage.removeItem(key));
  } catch {
    // Storage blocked — no-op.
  }
};

export const PROJECT_POLL_QUERY = "poll";
export const PROJECT_POLL_WIDGET_HASH = "quick-poll-widget";

export const getPostAuthReturnPath = (
  asPath: string,
  embed: EmbedContext,
  options: GetAuthRoutesOptions = {}
): string => {
  const projectPath = options.projectId ? `/${options.projectId}` : "";
  const returnToProject =
    !!projectPath &&
    (options.variant === "widget" ||
      embed.isInsideIframe ||
      isWidgetsOrEmbedPath(asPath));

  if (!returnToProject) return asPath;
  if (!options.quickPollId) return projectPath;

  return `${projectPath}?${PROJECT_POLL_QUERY}=${encodeURIComponent(
    options.quickPollId
  )}#${PROJECT_POLL_WIDGET_HASH}`;
};

export const getAuthRoutes = (
  asPath: string,
  embed: EmbedContext,
  options: GetAuthRoutesOptions = {}
): AuthRoutes => {
  const openInNewContext =
    embed.isEmbed &&
    embed.isInsideIframe &&
    !embed.isInsideIframeAndOnCurrentUrl;
  const nextPath = getPostAuthReturnPath(asPath, embed, options);

  return {
    login: `/login?next=${encodeURIComponent(nextPath)}`,
    register: `/register?next=${encodeURIComponent(nextPath)}`,
    target: openInNewContext ? "_blank" : "_parent"
  };
};
